import type React from "react";
import {memo, useCallback} from "react";
import type {CalendarComponents, CalendarDay, CalendarWeek} from "react-native-ui-datepicker";
import DateTimePicker from "react-native-ui-datepicker";

import {useColors} from "../../hooks/useColors";
import {ChevronLeftIcon} from "../../react/components/Icons/ChevronLeftIcon";
import {ChevronRightIcon} from "../../react/components/Icons/ChevronRightIcon";
import {Center} from "../ui/center";

import Day from "./Day";
import Weekday from "./Weekday";

const icons: CalendarComponents = {
    IconPrev: (
        <Center className="w-4 h-4 rounded-full bg-blueGray-200">
            <ChevronLeftIcon fill="blueGray.400" size="2xs" />
        </Center>
    ),
    IconNext: (
        <Center className="w-4 h-4 rounded-full bg-blueGray-200">
            <ChevronRightIcon fill="blueGray.400" size="2xs" />
        </Center>
    )
};

type Props = React.ComponentProps<typeof DateTimePicker> & {
    onMouseEnter?: (date: Date) => void;
    onSelectDay: (date: Date) => void;
};

const DateRangePickerUI: React.FC<Props> = ({
    className,
    classNames,
    startDate,
    endDate,
    onMouseEnter,
    onSelectDay,
    ...props
}) => {
    // Keep in mind, this way only works on the web
    const colors = useColors();

    const weekDay = useCallback((weekday: CalendarWeek) => {
        return <Weekday weekday={weekday} />;
    }, []);

    const day = useCallback(
        (day: CalendarDay) => {
            return <Day day={day} onMouseEnter={onMouseEnter} onSelectDay={onSelectDay} />;
        },
        [onMouseEnter, onSelectDay]
    );

    const cellSize = 48;

    return (
        <DateTimePicker
            showOutsideDays
            classNames={
                !process.env.IS_WEB
                    ? {
                          // those are handled on the Day component
                          // selected: "bg-primary-600 rounded-full",
                          // range_start: "bg-primary-600",
                          // range_end: "bg-primary-600",
                          day_cell: "my-0.5 h-[48px] w-[48px]",
                          range_fill: "bg-primary-50"
                      }
                    : undefined
            }
            components={{
                ...icons,
                Weekday: weekDay,
                Day: day
            }}
            // set null to ignore the default 300px
            // @ts-expect-error
            containerHeight={null}
            endDate={endDate}
            startDate={startDate}
            // This doesn't work on the web because the web uses the `swc` without the `nativewind/babel` plugin
            style={{maxWidth: 7 * cellSize}}
            styles={
                process.env.IS_WEB
                    ? {
                          day_cell: {
                              height: cellSize,
                              width: cellSize,
                              marginTop: 2,
                              marginBottom: 2
                          },
                          range_fill: {
                              backgroundColor: colors["--color-primary-50"]
                          }
                      }
                    : undefined
            }
            weekdaysHeight={28}
            {...props}
        />
    );
};

export default memo(DateRangePickerUI);
